import { ShellClientData } from '@suiteux/suiteux-shell';
import getSkypeApi from '../getSkypeApi';
import ISkypeApi from '../../store/schema/ISkypeApi';

interface UCWAPostData {
    status: number;
    responseText: string;
}

interface UCWAResponse {
    _links: UCWAApiLink;
    redirectClient: string;
}

interface UCWAApiLink {
    self: UCWAApiHref;
}

interface UCWAApiHref {
    href: string;
}

const SKYPE_ORIGIN_ROOT = '.lync.com';
const SKYPE_POLICY_API_PATH = '/policies';

const SKYPE_DIV_ID = 'SkypeDiv';
const SKYPE_DIV_SELECTOR = '#' + SKYPE_DIV_ID;
const INIT_MARKER_NAME = 'swx_content_initStart';

// This is a fallback method for checking Teams, it is based on UCWA.
// We only use this method when the main method doesn't work well.
export default async function checkForTeamsFallback(
    shellClientData: ShellClientData
): Promise<boolean> {
    // 1. Initial UCWA and get its API
    const api = await getSkypeApi();

    // 2. Set UCWA auth provider, this will insert the hidden IFRAME from XHR
    api.setAuthProvider({ type: 'implicitOAuth' });

    // 3. Create temp DIV for Skype
    const div = createDivForSkype();

    // 4. finally get the result
    const useTeams = await getIsTeamsEnabled(api, div);

    // 5. Clean up
    removeElement(div);

    // 6. Return the result
    return useTeams;
}

function createDivForSkype() {
    const div = document.createElement('div');
    div.id = SKYPE_DIV_ID;
    div.style.display = 'none';
    document.body.appendChild(div);

    return div;
}

function getIsTeamsEnabled(api: ISkypeApi, div: HTMLElement): Promise<boolean> {
    return new Promise<boolean>(resolve => {
        const onMessage = (e: MessageEvent) => {
            const isEnabled = parseMessage(e);
            if (isEnabled !== undefined) {
                window.removeEventListener('message', onMessage);

                resolve(isEnabled);
            }
        };

        window.addEventListener('message', onMessage);

        // Hack: to avoid js exception "The mark 'swx_content_initStart' does not exist.",
        // let's provide a start marker here (49834)
        // A full fix is tracked by 49738: UCMA: Find an elegant way to determine using Skype or Teams
        if (window.performance && window.performance.mark) {
            window.performance.mark(INIT_MARKER_NAME);
        }
        api.renderSidebar(SKYPE_DIV_SELECTOR);
    });
}

function parseMessage(e: MessageEvent) {
    const { origin, data } = e;
    if (origin && origin.indexOf(SKYPE_ORIGIN_ROOT) == origin.length - SKYPE_ORIGIN_ROOT.length) {
        let ucwaData: UCWAPostData;
        let ucwaResponse: UCWAResponse;
        try {
            ucwaData = JSON.parse(data) as UCWAPostData;
            if (ucwaData && ucwaData.status == 200) {
                ucwaResponse = JSON.parse(ucwaData.responseText) as UCWAResponse;
            }
        } catch {}

        if (ucwaResponse && ucwaResponse._links && ucwaResponse._links.self) {
            const href = ucwaResponse._links.self.href;
            if (
                href &&
                href.indexOf(SKYPE_POLICY_API_PATH) == href.length - SKYPE_POLICY_API_PATH.length
            ) {
                return ucwaResponse.redirectClient == 'Enabled';
            }
        }
    }

    return undefined;
}

function removeElement(element: HTMLElement) {
    if (element && element.parentNode) {
        element.parentNode.removeChild(element);
    }
}
